import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { Navbar } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import {
  Lock, Shield, KeyRound, Loader2, AlertTriangle, CheckCircle, Copy, Download, ArrowLeft, UserCircle,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Security settings — currently exposes the 2FA TOTP workflow.
 *
 *  - If 2FA is disabled: shows "Activar 2FA" → step 1 (QR + secret) →
 *    step 2 (verify code) → step 3 (download backup codes).
 *  - If 2FA is enabled: shows current status + counter of remaining
 *    backup codes + "Desactivar 2FA" button (requires a current code).
 */
export default function SecuritySettingsPage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  // Setup flow state
  const [stage, setStage] = useState('idle'); // idle | setup | verify | done
  const [setupData, setSetupData] = useState(null);
  const [code, setCode] = useState('');
  const [backupCodes, setBackupCodes] = useState(null);

  // Disable flow state
  const [disabling, setDisabling] = useState(false);
  const [disableCode, setDisableCode] = useState('');

  // Change password state
  const [pwForm, setPwForm] = useState({ next: '', confirm: '' });
  const [pwBusy, setPwBusy] = useState(false);

  const changePassword = async () => {
    if (!pwForm.next || !pwForm.confirm) {
      toast.error('Completa los dos campos.');
      return;
    }
    if (pwForm.next.length < 8) {
      toast.error('La nueva contraseña debe tener al menos 8 caracteres.');
      return;
    }
    if (pwForm.next !== pwForm.confirm) {
      toast.error('La confirmación no coincide con la nueva contraseña.');
      return;
    }
    setPwBusy(true);
    try {
      await axios.post(
        `${API_URL}/api/auth/change-password`,
        { new_password: pwForm.next },
        { withCredentials: true }
      );
      setPwForm({ next: '', confirm: '' });
      toast.success('Contraseña actualizada correctamente. El resto de sesiones abiertas se han cerrado.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo cambiar la contraseña');
    } finally {
      setPwBusy(false);
    }
  };

  useEffect(() => {
    if (!authLoading && !user) navigate('/login');
  }, [user, authLoading, navigate]);

  const refresh = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/auth/2fa/status`, { withCredentials: true });
      setStatus(res.data);
    } catch (err) {
      toast.error('No se pudo cargar el estado de seguridad');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (user) refresh(); }, [user]); // eslint-disable-line

  const startSetup = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${API_URL}/api/auth/2fa/setup`, {}, { withCredentials: true });
      setSetupData(res.data);
      setStage('setup');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo iniciar la configuración');
    } finally {
      setBusy(false);
    }
  };

  const verifyAndEnable = async () => {
    if (!code || code.length < 6) {
      toast.error('Introduce el código de 6 dígitos');
      return;
    }
    setBusy(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/auth/2fa/enable`,
        { code },
        { withCredentials: true },
      );
      setBackupCodes(res.data.backup_codes);
      setStage('done');
      setCode('');
      toast.success('2FA activado correctamente');
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Código incorrecto');
    } finally {
      setBusy(false);
    }
  };

  const disable2FA = async () => {
    if (!disableCode || disableCode.length < 6) {
      toast.error('Introduce un código válido o uno de respaldo');
      return;
    }
    setBusy(true);
    try {
      await axios.post(
        `${API_URL}/api/auth/2fa/disable`,
        { code: disableCode },
        { withCredentials: true },
      );
      toast.success('2FA desactivado');
      setDisabling(false);
      setDisableCode('');
      setBackupCodes(null);
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Código incorrecto');
    } finally {
      setBusy(false);
    }
  };

  const copyBackupCodes = () => {
    if (!backupCodes) return;
    navigator.clipboard.writeText(backupCodes.join('\n'));
    toast.success('Códigos copiados al portapapeles');
  };

  const downloadBackupCodes = () => {
    if (!backupCodes) return;
    const blob = new Blob(
      [
        'A Gift of Memories — Códigos de respaldo 2FA\n',
        `Cuenta: ${user.email}\n`,
        `Generados: ${new Date().toLocaleString('es-ES')}\n`,
        '\n',
        'Guarda estos códigos en un lugar seguro. Cada código sirve UNA sola vez si pierdes acceso a tu app autenticadora.\n\n',
        backupCodes.join('\n'),
      ],
      { type: 'text/plain' },
    );
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `agom-backup-codes-${user.email}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading || !status) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="pt-24 flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="pt-24 pb-12 px-4">
        <div className="max-w-2xl mx-auto">
          <button
            onClick={() => navigate('/dashboard')}
            className="text-sm text-muted-foreground hover:text-foreground mb-4 inline-flex items-center gap-1"
            data-testid="back-to-dashboard"
          >
            <ArrowLeft className="w-4 h-4" /> Volver al dashboard
          </button>

          <h1 className="font-heading text-3xl mb-1">Mi perfil</h1>
          <p className="text-muted-foreground mb-6">Tus datos personales y la seguridad de tu cuenta.</p>

          {/* ============ PERSONAL DATA CARD ============ */}
          <Card className="card-elevated mb-4" data-testid="profile-data-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <UserCircle className="w-5 h-5 text-muted-foreground" />
                Mis datos
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
                <div>
                  <p className="text-xs text-muted-foreground">Nombre</p>
                  <p className="font-medium" data-testid="profile-name">{user?.name || '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Email</p>
                  <p className="font-medium" data-testid="profile-email">{user?.email || '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Teléfono</p>
                  <p className="font-medium" data-testid="profile-phone">{user?.phone || '—'}</p>
                </div>
                {user?.billing_profile && (
                  <div>
                    <p className="text-xs text-muted-foreground">
                      Datos de facturación ({user.billing_profile.type === 'empresa' ? 'Empresa' : 'Particular'})
                    </p>
                    <p className="font-medium" data-testid="profile-billing">
                      {user.billing_profile.nif} · {user.billing_profile.address}, {user.billing_profile.postal_code} {user.billing_profile.city}
                    </p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* ============ SECURITY SECTION ============ */}
          <h2 className="font-heading text-xl mt-8 mb-3 flex items-center gap-2" data-testid="security-section-title">
            <Shield className="w-5 h-5 text-primary" />
            Seguridad
          </h2>

          {/* ============ CHANGE PASSWORD CARD ============ */}
          <Card className="card-elevated mb-4" data-testid="change-password-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <KeyRound className="w-5 h-5 text-muted-foreground" />
                Cambiar contraseña
              </CardTitle>
              <CardDescription>
                Al cambiarla, se cerrarán el resto de sesiones abiertas en otros dispositivos.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-sm">Nueva contraseña</Label>
                  <Input
                    type="password"
                    value={pwForm.next}
                    onChange={(e) => setPwForm({ ...pwForm, next: e.target.value })}
                    autoComplete="new-password"
                    placeholder="Mínimo 8 caracteres"
                    className="mt-1"
                    data-testid="new-password-input"
                  />
                </div>
                <div>
                  <Label className="text-sm">Repite la nueva contraseña</Label>
                  <Input
                    type="password"
                    value={pwForm.confirm}
                    onChange={(e) => setPwForm({ ...pwForm, confirm: e.target.value })}
                    autoComplete="new-password"
                    className="mt-1"
                    data-testid="confirm-password-input"
                  />
                </div>
              </div>
              <Button
                onClick={changePassword}
                disabled={pwBusy}
                className="btn-primary gap-2"
                data-testid="change-password-btn"
              >
                {pwBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />}
                Cambiar contraseña
              </Button>
            </CardContent>
          </Card>

          {/* ============ 2FA STATUS CARD ============ */}
          <Card className="card-elevated mb-4" data-testid="twofa-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className={`w-5 h-5 ${status.enabled ? 'text-emerald-600' : 'text-muted-foreground'}`} />
                Verificación en 2 pasos (2FA)
                {status.enabled && (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 font-medium">
                    Activado
                  </span>
                )}
              </CardTitle>
              <CardDescription>
                Añade una capa extra de seguridad usando una app autenticadora (Google Authenticator, Authy, 1Password…).
              </CardDescription>
            </CardHeader>

            <CardContent>
              {/* ===== Enabled: show status + disable ===== */}
              {status.enabled && !disabling && (
                <div className="space-y-3">
                  <div className="flex items-start gap-2 text-sm">
                    <CheckCircle className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-medium">2FA está activado en tu cuenta.</p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Te quedan <strong>{status.backup_codes_remaining}</strong> códigos de respaldo de 8.
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    onClick={() => setDisabling(true)}
                    className="gap-2 border-rose-300 text-rose-700 hover:bg-rose-50"
                    data-testid="twofa-disable-btn"
                  >
                    <Lock className="w-4 h-4" /> Desactivar 2FA
                  </Button>
                </div>
              )}

              {/* ===== Disabling form ===== */}
              {status.enabled && disabling && (
                <div className="space-y-3 p-4 rounded-md bg-rose-50/50 border border-rose-200">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-rose-700 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-rose-900">
                      Introduce el código actual de tu app (o un código de respaldo) para confirmar la desactivación.
                    </p>
                  </div>
                  <Input
                    type="text"
                    value={disableCode}
                    onChange={(e) => setDisableCode(e.target.value)}
                    placeholder="123456 o XXXX-XXXX"
                    className="font-mono"
                    data-testid="twofa-disable-code"
                  />
                  <div className="flex gap-2">
                    <Button onClick={disable2FA} disabled={busy} variant="destructive" className="gap-2" data-testid="twofa-disable-confirm">
                      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
                      Desactivar definitivamente
                    </Button>
                    <Button variant="ghost" onClick={() => { setDisabling(false); setDisableCode(''); }}>
                      Cancelar
                    </Button>
                  </div>
                </div>
              )}

              {/* ===== Not enabled, idle ===== */}
              {!status.enabled && stage === 'idle' && (
                <div className="space-y-3">
                  <p className="text-sm text-muted-foreground">
                    Cuando lo actives, además de tu contraseña te pediremos un código de 6 dígitos cada vez que inicies sesión.
                  </p>
                  <Button onClick={startSetup} disabled={busy} className="btn-primary gap-2" data-testid="twofa-setup-btn">
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Shield className="w-4 h-4" />}
                    Activar 2FA
                  </Button>
                </div>
              )}

              {/* ===== Setup: QR + verify ===== */}
              {!status.enabled && (stage === 'setup' || stage === 'verify') && setupData && (
                <div className="space-y-4">
                  <div className="text-sm space-y-1">
                    <p className="font-medium">Paso 1 — Escanea este código con tu app:</p>
                    <p className="text-xs text-muted-foreground">Compatible con Google Authenticator, Authy, 1Password, Microsoft Authenticator, etc.</p>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-4 items-start p-4 bg-muted/30 rounded-md">
                    <img
                      src={`${API_URL}/api/auth/2fa/qr.png`}
                      alt="QR 2FA"
                      className="w-48 h-48 bg-white p-2 rounded border"
                      data-testid="twofa-qr-img"
                    />
                    <div className="flex-1 text-xs space-y-2">
                      <p className="font-medium">¿No puedes escanear?</p>
                      <p className="text-muted-foreground">Añade la cuenta manualmente:</p>
                      <div className="bg-white p-2 rounded border font-mono break-all" data-testid="twofa-manual-key">
                        {setupData.manual_entry_key}
                      </div>
                      <button
                        onClick={() => { navigator.clipboard.writeText(setupData.manual_entry_key); toast.success('Copiado'); }}
                        className="text-primary hover:underline flex items-center gap-1"
                      >
                        <Copy className="w-3 h-3" /> Copiar clave
                      </button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="setup-code" className="text-sm font-medium">
                      Paso 2 — Introduce el código que muestra tu app:
                    </Label>
                    <Input
                      id="setup-code"
                      type="text"
                      autoComplete="one-time-code"
                      inputMode="numeric"
                      value={code}
                      onChange={(e) => setCode(e.target.value)}
                      placeholder="123456"
                      className="font-mono tracking-[0.5em] text-center text-lg"
                      data-testid="twofa-setup-code"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={verifyAndEnable}
                      disabled={busy || code.length < 6}
                      className="btn-primary gap-2"
                      data-testid="twofa-enable-btn"
                    >
                      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                      Verificar y activar
                    </Button>
                    <Button variant="ghost" onClick={() => { setStage('idle'); setSetupData(null); setCode(''); }}>
                      Cancelar
                    </Button>
                  </div>
                </div>
              )}

              {/* ===== Done: backup codes ===== */}
              {stage === 'done' && backupCodes && (
                <div className="space-y-3 p-4 rounded-md bg-amber-50/60 border border-amber-300">
                  <div className="flex items-start gap-2">
                    <KeyRound className="w-5 h-5 text-amber-700 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-amber-900">Guarda estos 8 códigos de respaldo</p>
                      <p className="text-xs text-amber-800 mt-0.5">
                        Si pierdes acceso a tu app autenticadora, podrás usar uno de estos códigos (UNA sola vez cada uno) para entrar. <strong>No los podemos volver a mostrar.</strong>
                      </p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 my-3">
                    {backupCodes.map((c) => (
                      <code key={c} className="bg-white p-2 rounded border text-xs font-mono text-center" data-testid={`backup-code-${c}`}>
                        {c}
                      </code>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={copyBackupCodes} className="gap-1 text-xs" data-testid="backup-copy">
                      <Copy className="w-3 h-3" /> Copiar al portapapeles
                    </Button>
                    <Button variant="outline" size="sm" onClick={downloadBackupCodes} className="gap-1 text-xs" data-testid="backup-download">
                      <Download className="w-3 h-3" /> Descargar como TXT
                    </Button>
                  </div>
                  <Button
                    onClick={() => { setStage('idle'); setBackupCodes(null); }}
                    className="btn-primary w-full mt-2"
                    data-testid="backup-acknowledge"
                  >
                    Ya los he guardado en un lugar seguro
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
